import { useMutation, UseMutationConfig } from '@libs/use';
import {
  CreateConditionalOrderRequest,
  CreateOrderResponse,
} from '@libs/api-reference';
import { unref } from 'vue';
import { MaybeRef } from '@vueuse/core';

import * as API from '../requests';
import {
  ORDER_SIDES,
  CONDITIONAL_CREATION_HOUR,
  CONDITIONAL_ORDER_CLIENT_TAG,
} from '../constants';

export type CreateConditionalOrderFormModel = {
  quantity: number;
  totalValue: number;
  upperThreshold: number;
  lowerThreshold: number;
  validityDate: number;
  bypassSellQuantity: boolean;
};

type ConditionalOrderFactoryOptions = {
  formModel: CreateConditionalOrderFormModel;
  orderExecutionType: string;
  symbol: MaybeRef<string>;
  type: MaybeRef<'buy' | 'sell'>;
};

type UseCreateConditionalOrderOptions = {
  orderExecutionType: string;
  symbol: ConditionalOrderFactoryOptions['symbol'];
  type: ConditionalOrderFactoryOptions['type'];
  onReject?: UseMutationConfig<CreateOrderResponse>['onReject'];
  onSuccess?: UseMutationConfig<CreateOrderResponse>['onSuccess'];
};

function orderConditionalFactory({
  formModel,
  symbol,
  type,
  orderExecutionType,
}: ConditionalOrderFactoryOptions): CreateConditionalOrderRequest {
  const today = new Date();

  return {
    instrumentIdentification: unref(symbol),
    orderSide: ORDER_SIDES[unref(type)],
    quantity: formModel.quantity,
    value: formModel.totalValue,
    upperThreshold: formModel.upperThreshold,
    lowerThreshold: formModel.lowerThreshold,
    orderExecutionType,
    clientTag: CONDITIONAL_ORDER_CLIENT_TAG,
    validityDate: new Date(
      new Date(
        today.setDate(today.getDate() + formModel.validityDate)
      ).setHours(CONDITIONAL_CREATION_HOUR, 59, 59)
    ),
    bypassSellQuantity: formModel.bypassSellQuantity,
  };
}

export default function useCreateConditionalOrderQuery({
  symbol,
  type,
  orderExecutionType,
  onReject,
  onSuccess,
}: UseCreateConditionalOrderOptions) {
  const {
    execute: createConditionalOrder,
    isError: isErrorCreateConditionalOrder,
    isPending: isLoadingCreateConditionalOrder,
    ...query
  } = useMutation<object>(
    ({ formModel }) =>
      API.createConditionalOrder(
        orderConditionalFactory({ formModel, symbol, type, orderExecutionType })
      ),
    {
      onSuccess,
      onReject,
    }
  );

  return {
    createConditionalOrder,
    isLoadingCreateConditionalOrder,
    isErrorCreateConditionalOrder,
    ...query,
  };
}
